import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Hook for managing collaborative state following the syncNodeData pattern.
 * Prevents data loss and race conditions by properly separating local UI state 
 * from collaborative state synchronization.
 *  * @param field - The field name in the node data
 * @param defaultValue - Default value when no data exists
 * @param initialValue - Initial value from collaborative state
 * @param syncNodeData - Function to sync data to collaborative state
 */
export function useCollaborativeState<T>(
    field: string,
    defaultValue: T,
    initialValue: T | undefined,
    syncNodeData: (data: Record<string, any>) => void
) {
    // Local state for immediate UI responsiveness
    const [localValue, setLocalValue] = useState<T>(defaultValue)

    // Track the last synced value to detect external changes
    const lastSyncedValue = useRef<T>(initialValue ?? defaultValue)

    // Sync local state when collaborative state changes externally
    useEffect(() => {
        const hasExternalChange = initialValue !== lastSyncedValue.current
        if (hasExternalChange && initialValue !== undefined) {
            setLocalValue(initialValue)
            lastSyncedValue.current = initialValue
        }
    }, [initialValue])

    // Function to sync current local value to collaborative state
    const syncToCollaborative = useCallback(() => {
        syncNodeData({ [field]: localValue })
        lastSyncedValue.current = localValue
    }, [field, localValue, syncNodeData])

    // Function to update local state and optionally sync immediately
    const updateValue = useCallback((newValue: T, shouldSync = false) => {
        setLocalValue(newValue)
        if (shouldSync) {
            // Use the new value directly instead of local state to avoid stale closure
            syncNodeData({ [field]: newValue })
            lastSyncedValue.current = newValue
        }
    }, [field, syncNodeData])

    return {
        /** Current local value for UI display */
        value: localValue,
        /** Update local state only (for immediate UI response) */
        setValue: setLocalValue,
        /** Update local state and optionally sync to collaborative state */
        updateValue,
        /** Sync current local value to collaborative state */
        syncToCollaborative,
        /** Whether the local value differs from last synced value */
        hasUnsyncedChanges: localValue !== lastSyncedValue.current
    }
}
